/* TENUTA · NEI RIBASSI · INTORNO, presi dal backtest corrispondente al bot.
 *
 * Due cose vanno dette a schermo e non date per scontate:
 *
 *  - questi numeri NON misurano il bot: misurano il backtest da cui il bot
 *    viene, su un altro periodo. Scriverli accanto al profitto del bot senza
 *    dirlo li farebbe leggere come risultati live, che è esattamente il modo di
 *    sbagliare che la regola 2 esiste per impedire;
 *  - quando la corrispondenza non c'è, la ragione va detta per intero. "Non
 *    trovato" copre tre situazioni diverse — non è mai stato provato, non
 *    abbiamo cercato abbastanza, il bot non è confrontabile — e nessuna delle
 *    tre è "storia insufficiente", che è il messaggio che un `n/d` porterebbe.
 */

import { Link } from 'react-router-dom';
import {
  Avvertenza,
  Distintivo,
  Intorno,
  NeiRibassi,
  Tenuta,
  divisioneDaMetodo,
} from '../../componenti';
import { durataPeriodo, num, periodo as etichettaPeriodo } from '../../lib/formato';
import { leggiNeiRibassi, leggiTenuta, type LetturaSoglia } from '../../lib/metriche';
import type { StatoDati } from '../../api';
import type { Correlazione } from './correlazione';

/** Il periodo del BACKTEST, non quello del bot. La differenza è il punto. */
function periodoBacktest(c: Correlazione): string {
  if (!c.riga) return '—';
  const p = etichettaPeriodo(c.riga.startDate, c.riga.endDate);
  const d = durataPeriodo(c.riga.startDate, c.riga.endDate);
  return d ? `${p} · ${d}` : p;
}

/* ==========================================================================
 * Perché la corrispondenza non c'è
 * ======================================================================== */

export function MotivoSenzaBacktest({
  correlazione: c,
  stato,
}: {
  correlazione: Correlazione | undefined;
  stato: StatoDati;
}) {
  if (stato === 'caricamento') {
    return <div className="sb-nota">Cerco il backtest con questi parametri…</div>;
  }

  if (stato === 'errore') {
    return (
      <Avvertenza livello="cautela">
        La ricerca del backtest corrispondente non è riuscita: Tenuta, Nei ribassi e Intorno
        restano sconosciuti finché non si rilegge.
      </Avvertenza>
    );
  }

  if (!c || c.stato === 'non-confrontabile') {
    return (
      <Avvertenza livello="cautela">
        <b>Bot non confrontabile.</b> Non ha <code>strategyParams</code>: senza i parametri non c’è
        niente da cercare, e qualunque backtest gli somigliasse sarebbe un accostamento inventato.
      </Avvertenza>
    );
  }

  if (c.stato === 'assente') {
    return (
      <Avvertenza livello="cautela">
        <b>Nessun backtest con questi parametri.</b> Cercati tutti i {num(c.totali)} backtest della
        stessa strategia, coppia e candela: questa configurazione non è mai stata provata. Non è
        “storia insufficiente” — è che il bot opera su parametri che nessuna prova ha giustificato.
      </Avvertenza>
    );
  }

  return (
    <Avvertenza livello="cautela">
      <b>Ricerca fermata prima della fine.</b> Guardate {num(c.esaminate)} righe su {num(c.totali)}:
      il backtest potrebbe esserci più avanti. Non si può concludere né che esista né che manchi,
      perché il server non sa filtrare per parametri di strategia.
    </Avvertenza>
  );
}

/* ==========================================================================
 * I tre indicatori, forma piena (dettaglio)
 * ======================================================================== */

export function IndicatoriBacktest({
  correlazione: c,
  stato,
}: {
  correlazione: Correlazione | undefined;
  stato: StatoDati;
}) {
  if (stato !== 'pieno' || !c || c.stato !== 'trovato' || !c.riga) {
    return <MotivoSenzaBacktest correlazione={c} stato={stato} />;
  }

  const r = c.riga;
  const p = periodoBacktest(c);

  return (
    <>
      <div className="tb-provenienza">
        <span>
          Misurati sul backtest <Link to={`/backtest/${r.id}`}>#{r.id}</Link>
          {r.backtestGroupId != null
            ? ` del gruppo #${r.backtestGroupId}`
            : ' (singolo, senza griglia)'}
          , non sul bot in esecuzione.
        </span>
        {c.candidati > 1 && (
          <Distintivo contorno>{c.candidati} backtest con questi parametri</Distintivo>
        )}
      </div>

      <Tenuta
        valore={r.walkForwardScore}
        divisione={divisioneDaMetodo(r.walkForwardMetodo)}
        periodo={p}
      />

      <NeiRibassi valore={r.bearYearsAvg} anniNegativi={r.bearYearsCount} periodo={p} />

      <Intorno
        valore={r.intorno}
        parametroPiuFragile={r.intornoParametro}
        assi={r.intornoAssi}
        periodo={p}
      />
    </>
  );
}

/* ==========================================================================
 * Forma fitta (scheda di lista e cella di tabella)
 *
 * Gli stessi quattro pezzi della forma piena — colore, valore, verdetto,
 * qualificatore — solo più stretti. Non è una versione con dei pezzi in meno:
 * una soglia senza il suo qualificatore è un numero di cui non si sa se
 * fidarsi, e la lista è proprio il posto dove si sceglie quale bot aprire.
 * ======================================================================== */

export function SogliaFitta({ titolo, lettura }: { titolo: string; lettura: LetturaSoglia }) {
  return (
    <div className={`tb-soglia-fitta tb-soglia-fitta--${lettura.tono}`}>
      <div className="tb-soglia-fitta__testata">
        <span className="tb-soglia-fitta__titolo">{titolo}</span>
        <span className={`tb-soglia-fitta__valore sb-tono-${lettura.tono}`}>{lettura.valore}</span>
      </div>
      <div className={`tb-soglia-fitta__verdetto sb-tono-${lettura.tono}`}>{lettura.verdetto}</div>
      <div
        className={`tb-soglia-fitta__qualificatore${
          lettura.qualificatore.fragile ? ' tb-soglia-fitta__qualificatore--fragile' : ''
        }`}
      >
        {lettura.qualificatore.testo}
      </div>
    </div>
  );
}

/**
 * Le due soglie che il prototipo mostra su ogni riga di bot.
 * L'intorno resta al dettaglio: in lista sarebbe la terza scheda su una riga
 * che ne ha già cinque, e non è il numero con cui si sceglie quale bot aprire.
 */
export function IndicatoriFitti({
  correlazione: c,
  stato,
}: {
  correlazione: Correlazione | undefined;
  stato: StatoDati;
}) {
  if (stato === 'caricamento') return <div className="sb-nota">Cerco il backtest…</div>;

  if (!c || c.stato !== 'trovato' || !c.riga) {
    return <MotivoBreve correlazione={c} stato={stato} />;
  }

  const r = c.riga;

  return (
    <div className="tb-soglie-fitte">
      <SogliaFitta
        titolo="TENUTA"
        lettura={leggiTenuta(r.walkForwardScore, divisioneDaMetodo(r.walkForwardMetodo))}
      />
      <SogliaFitta
        titolo="NEI RIBASSI"
        lettura={leggiNeiRibassi(r.bearYearsAvg, r.bearYearsCount)}
      />
      <div className="sb-nota tb-soglie-fitte__periodo">
        dal backtest #{r.id} · {periodoBacktest(c)}
      </div>
    </div>
  );
}

/** In lista il motivo sta in una riga sola: la versione per esteso è nel
 *  dettaglio, dove c'è lo spazio per spiegarla. */
function MotivoBreve({
  correlazione: c,
  stato,
}: {
  correlazione: Correlazione | undefined;
  stato: StatoDati;
}) {
  const testo =
    stato === 'errore'
      ? 'ricerca del backtest non riuscita'
      : !c || c.stato === 'non-confrontabile'
        ? 'bot senza parametri, non confrontabile'
        : c.stato === 'assente'
          ? 'nessun backtest con questi parametri'
          : `non trovato nelle prime ${num(c.esaminate)} righe di ${num(c.totali)}`;

  return <div className="tb-senza-backtest">Tenuta e Nei ribassi: {testo}</div>;
}
